// pages/sessions/PublicSession.js
import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { io } from 'socket.io-client'
import api from '../../services/api'

export default function PublicSession() {
  const [searchParams] = useSearchParams()
  const [barcodeId, setBarcodeId] = useState('')
  const [sessionId, setSessionId] = useState('')
  const [status, setStatus] = useState('')
  const [languages, setLanguages] = useState([])
  const [selectedLang, setSelectedLang] = useState(null)
  const [typedText, setTypedText] = useState('')
  const [messages, setMessages] = useState([])
  const [recording, setRecording] = useState(false)
  const audioContextRef = useRef(null)
  const sourceRef = useRef(null)
  const processorRef = useRef(null)
  const mediaStreamRef = useRef(null)
  const socketRef = useRef(null)

  useEffect(() => {
    const bId = searchParams.get('barcodeId') || ''
    setBarcodeId(bId)
    loadLanguages()
  }, [searchParams])

  useEffect(() => {
    if (sessionId) {
      socketRef.current = io('http://localhost:5111')
      socketRef.current.emit('joinSession', sessionId)
      socketRef.current.on('newMessage', data => {
        if (data.sessionId === sessionId) {
          setMessages(prev => [...prev, { sender: data.sender, text: data.text }])
        }
      })
      loadSessionMessages()
    }
    return () => {
      if (socketRef.current) socketRef.current.disconnect()
    }
  }, [sessionId])

  const loadSessionMessages = async () => {
    try {
      const r = await api.get(`/session/${sessionId}`)
      setMessages(r.data.session.messages)
    } catch (err) {
      console.log(err)
    }
  }

  const loadLanguages = async () => {
    try {
      const r = await api.get('/language')
      setLanguages(r.data.languages)
    } catch (err) {
      console.log(err)
    }
  }

  const createSession = async () => {
    if (!barcodeId) {
      setStatus('No barcode')
      return
    }
    try {
      const r = await api.post('/session/start-barcode', { barcodeId })
      setSessionId(r.data.sessionId)
      setStatus(r.data.message)
    } catch (err) {
      console.log(err)
      setStatus(err.response?.data?.error || 'Error')
    }
  }

  const handleLanguageChange = async langId => {
    const chosen = languages.find(l => l._id === langId)
    if (!chosen) {
      setStatus('Invalid language')
      return
    }
    setSelectedLang(chosen)
    if (!sessionId) {
      setStatus('No session started')
      return
    }
    try {
      const r = await api.post('/session/select-language', {
        sessionId,
        guestLanguageId: chosen._id
      })
      setStatus(r.data.message)
      speakText('Hello, welcome. You can speak or type your request.', sessionId)
    } catch (err) {
      console.log(err)
      setStatus(err.response?.data?.error || 'Error')
    }
  }

  const speakText = async (text, sid) => {
    if (!sid) return
    try {
      const r = await api.post('/session/speak', { sessionId: sid, text }, { responseType: 'arraybuffer' })
      const blob = new Blob([r.data], { type: 'audio/mpeg' })
      const url = URL.createObjectURL(blob)
      const a = new Audio(url)
      a.play()
    } catch (err) {
      console.log(err)
    }
  }

  const handleSendTyped = async () => {
    if (!typedText.trim()) {
      setStatus('Empty text')
      return
    }
    if (!sessionId) {
      setStatus('No session started')
      return
    }
    try {
      const r = await api.post('/session/post-message', {
        sessionId,
        sender: 'guest',
        text: typedText.trim()
      })
      setTypedText('')
      setStatus(r.data.message)
    } catch (err) {
      console.log(err)
      setStatus(err.response?.data?.error || 'Error')
    }
  }

  const startCapture = async () => {
    if (!sessionId) {
      setStatus('No session started')
      return
    }
    if (recording) return
    setStatus('')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream
      audioContextRef.current = new AudioContext({ sampleRate: 16000 })
      await audioContextRef.current.audioWorklet.addModule('/broadcastProcessor.js')
      processorRef.current = new AudioWorkletNode(audioContextRef.current, 'broadcast-processor')
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream)
      sourceRef.current.connect(processorRef.current)
      processorRef.current.port.onmessage = e => handleSamples(e.data, sessionId)
      setRecording(true)
    } catch (err) {
      console.log(err)
      setStatus('Microphone error')
    }
  }

  const handleSamples = (float32Samples, sid) => {
    const pcm16 = new Int16Array(float32Samples.length)
    for (let i = 0; i < float32Samples.length; i++) {
      let s = float32Samples[i]
      if (s > 1) s = 1
      if (s < -1) s = -1
      pcm16[i] = s < 0 ? s * 32768 : s * 32767
    }
    const base64 = btoa(String.fromCharCode(...new Uint8Array(pcm16.buffer)))
    api.post('/session/transcribe', { sessionId: sid, audioBase64: base64 }).catch(err => console.log(err))
  }

  const stopCapture = async () => {
    if (!recording) return
    setRecording(false)
    if (processorRef.current) processorRef.current.disconnect()
    if (sourceRef.current) sourceRef.current.disconnect()
    if (audioContextRef.current) await audioContextRef.current.close()
    if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(t => t.stop())
    try {
      if (sessionId) await api.post('/session/transcribe', { sessionId, audioBase64: '' })
    } catch (err) {
      console.log(err)
    }
    processorRef.current = null
    sourceRef.current = null
    audioContextRef.current = null
    mediaStreamRef.current = null
  }

  return (
    <div>
      <h2>Public Session</h2>
      {!sessionId && <p>Barcode ID: {barcodeId}</p>}
      {!sessionId && <button onClick={createSession}>Start Communication Session</button>}
      {sessionId && <p>Session ID: {sessionId}</p>}
      <label>Select Language</label>
      <select onChange={e => handleLanguageChange(e.target.value)}>
        <option value="">Choose language</option>
        {languages.map(l => (
          <option key={l._id} value={l._id}>
            {l.name} ({l.code}, {l.awsCode})
          </option>
        ))}
      </select>
      <p>{recording ? 'Recording...' : 'Press to speak'}</p>
      {!recording && <button onClick={startCapture}>Speak</button>}
      {recording && <button onClick={stopCapture}>Stop</button>}
      <br/>
      <textarea
        placeholder="Type message"
        value={typedText}
        onChange={e => setTypedText(e.target.value)}
      />
      <button onClick={handleSendTyped}>Send Typed Message</button>
      <p>{status}</p>
      {messages.map((m, i) => (
        <div key={i}>
          <strong>{m.sender}:</strong> {m.text}
        </div>
      ))}
    </div>
  )
}